import clsx from 'clsx'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import InputWithTooltipPlaceholder from './InputWithTooltipPlaceholder'

/**
 * Ported from pitstop-ui/src/components/EForm/FormField/cell-string.jsx
 *
 * Changes:
 *   - `classnames` → `clsx`
 *   - moment + react-datepicker removed (not installed in submittals-ui):
 *     date fields fall back to a plain disabled input showing the raw value
 *   - calendar.svg / iconCloseGrey.svg removed (not in submittals-ui/assets/svgs)
 *   - InputWithTooltipPlaceholder import path corrected to local ./
 */

const INPUT_BASE =
  'box-border w-full h-[45px] pt-3 px-[14px] pb-[10px] type-body-2 text-grey-10 border border-grey-90 rounded-[5px] bg-white shadow-none transition-[border-color] duration-150 ease-in-out appearance-none focus:border-theme-50 focus:outline-none disabled:text-grey-50 disabled:cursor-not-allowed disabled:pointer-events-none disabled:bg-grey-98'

function getClassName(error) {
  return clsx(INPUT_BASE, error && 'border-red-50 focus:border-red-50')
}

const CellStringComponent = ({
  error,
  isReadOnly,
  schema,
  payload,
  parentProps,
  inputProps,
  setInputProps,
  updatePayload,
  validateSchema,
  getFieldValue,
}) => {
  useEffect(() => {
    setInputProps({
      ...inputProps,
      disabled: isReadOnly,
      placeholder: isReadOnly ? null : schema.description || schema.title || '',
      value:
        getFieldValue(payload, parentProps) === null ? '' : getFieldValue(payload, parentProps),
      onChange,
    })
  }, [payload, isReadOnly])

  function onChange(e) {
    if (isReadOnly) return
    const value = e?.target?.value ?? ''
    setInputProps({ ...inputProps, value })
    updatePayload(value)
    validateSchema(value)
  }

  // Date fields: show raw string value (no datepicker without moment/react-datepicker)
  if (schema.format?.includes('date')) {
    const displayValue =
      getFieldValue(payload, parentProps) === null
        ? ''
        : (getFieldValue(payload, parentProps) ?? '')
    return (
      <input
        className={getClassName(error)}
        disabled
        value={displayValue}
        readOnly
        data-testid="eform-date-display"
        onChange={() => {}}
      />
    )
  }

  // Enum fields: show selected value in a disabled styled input
  if (schema.enum) {
    return (
      <input
        className={getClassName(error)}
        disabled
        value={inputProps?.value ?? ''}
        readOnly
        onChange={() => {}}
        data-testid="eform-enum-display"
      />
    )
  }

  return (
    <InputWithTooltipPlaceholder
      {...inputProps}
      onChange={onChange}
      className={getClassName(error)}
    />
  )
}

CellStringComponent.propTypes = {
  error: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  schema: PropTypes.object,
  payload: PropTypes.object,
  parentProps: PropTypes.array,
  inputProps: PropTypes.object,
  setInputProps: PropTypes.func,
  updatePayload: PropTypes.func,
  validateSchema: PropTypes.func,
  getFieldValue: PropTypes.func,
  setEFormFieldError: PropTypes.func,
  setIsValidFormat: PropTypes.func,
  required: PropTypes.bool,
  propertyName: PropTypes.string,
}

export default CellStringComponent
